import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Switch,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StoreIcon from "@mui/icons-material/Store";
import { fetchSucursales } from "../../../../RRHH2/features/legajos/services/empleados.api";
import {
  getProveedoresPorSucursal,
  asociarProveedorASucursal,
  cambiarEstadoAsociacion,
} from "../service/proveedoresService";

const SucursalesProveedorModal = ({ open, onClose, proveedor, showToast }) => {
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open || !proveedor) return;

    const cargar = async () => {
      setLoading(true);
      try {
        const sucursales = (await fetchSucursales()) || [];
        const asociaciones = await Promise.all(
          sucursales.map((s) => getProveedoresPorSucursal(s.id).catch(() => [])),
        );

        const nuevasFilas = sucursales.map((sucursal, i) => {
          const asociacion = (asociaciones[i] || []).find(
            (a) => a.id_proveedor === proveedor.id,
          );
          return { sucursal, asociacion: asociacion || null };
        });

        setFilas(nuevasFilas);
      } catch (err) {
        showToast(err.message || "Error al cargar las sucursales.", "error");
      } finally {
        setLoading(false);
      }
    };

    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proveedor]);

  const handleAsociar = async (sucursal) => {
    setBusyId(sucursal.id);
    try {
      const res = await asociarProveedorASucursal(sucursal.id, proveedor.id);
      setFilas((prev) =>
        prev.map((f) =>
          f.sucursal.id === sucursal.id
            ? { ...f, asociacion: { id: res.id, activo: 1 } }
            : f,
        ),
      );
      showToast(`${proveedor.nombre} asociado a ${sucursal.nombre}.`, "success");
    } catch (err) {
      showToast(err.message || "Error al asociar el proveedor.", "error");
    } finally {
      setBusyId(null);
    }
  };

  const handleToggle = async (fila, checked) => {
    setBusyId(fila.sucursal.id);
    try {
      await cambiarEstadoAsociacion(fila.asociacion.id, checked ? 1 : 0);
      setFilas((prev) =>
        prev.map((f) =>
          f.sucursal.id === fila.sucursal.id
            ? { ...f, asociacion: { ...f.asociacion, activo: checked ? 1 : 0 } }
            : f,
        ),
      );
    } catch (err) {
      showToast(err.message || "Error al actualizar la asociación.", "error");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StoreIcon color="secondary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Sucursales de {proveedor?.nombre}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List disablePadding>
            {filas.map((fila, i) => (
              <Box key={fila.sucursal.id}>
                {i > 0 && <Divider component="li" />}
                <ListItem
                  secondaryAction={
                    fila.asociacion ? (
                      <Switch
                        checked={Number(fila.asociacion.activo) === 1}
                        disabled={busyId === fila.sucursal.id}
                        onChange={(e) => handleToggle(fila, e.target.checked)}
                      />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busyId === fila.sucursal.id}
                        onClick={() => handleAsociar(fila.sucursal)}
                        sx={{ textTransform: "none" }}
                      >
                        Asociar
                      </Button>
                    )
                  }
                >
                  <ListItemText
                    primary={fila.sucursal.nombre}
                    secondary={
                      fila.asociacion
                        ? Number(fila.asociacion.activo) === 1
                          ? "Puede pedirle a este proveedor"
                          : "Asociación desactivada"
                        : "Sin asociar"
                    }
                  />
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SucursalesProveedorModal;
